'use client';

/**
 * Form component for collecting name and email from anonymous users
 */

import React, { useState, useCallback, KeyboardEvent } from 'react';

export interface UserInfoFormData {
  name: string;
  email: string;
}

interface UserInfoFormProps {
  onSubmit: (data: UserInfoFormData) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function UserInfoForm({
  onSubmit,
  isLoading = false,
  error: externalError = null,
}: UserInfoFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = useCallback((emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue.trim());
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: { name?: string; email?: string } = {};

    // Validate name
    const trimmedName = name.trim();
    if (!trimmedName) {
      newErrors.name = 'Name is required';
    } else if (trimmedName.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (trimmedName.length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    // Validate email
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, email, validateEmail]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
      }

      if (!validate()) {
        return;
      }

      setIsSubmitting(true);
      try {
        await onSubmit({
          name: name.trim(),
          email: email.trim(),
        });
      } catch (err) {
        console.error('Form submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [name, email, validate, onSubmit]
  );

  const handleKeyPress = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !isSubmitting && !isLoading) {
        handleSubmit();
      }
    },
    [handleSubmit, isSubmitting, isLoading]
  );

  const displayError = externalError || errors.name || errors.email;

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
        backgroundColor: '#ffffff',
      }}
    >
      <div
        style={{
          marginBottom: '24px',
        }}
      >
        <h2
          style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '8px',
          }}
        >
          Welcome! 👋
        </h2>
        <p
          style={{
            fontSize: '14px',
            color: '#6b7280',
            lineHeight: '1.5',
          }}
        >
          To get started, please provide your name and email address. This helps us connect with you and provide better support.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ marginBottom: '16px' }}>
          <label
            htmlFor="user-info-name"
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              marginBottom: '6px',
            }}
          >
            Name *
          </label>
          <input
            id="user-info-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) {
                setErrors((prev) => ({ ...prev, name: undefined }));
              }
            }}
            onKeyPress={handleKeyPress}
            disabled={isSubmitting || isLoading}
            placeholder="Enter your name"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: `1px solid ${errors.name ? '#ef4444' : '#d1d5db'}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              outline: 'none',
              transition: 'border-color 0.2s',
              backgroundColor: isSubmitting || isLoading ? '#f9fafb' : '#ffffff',
              color: '#111827',
            }}
            aria-invalid={errors.name ? 'true' : 'false'}
            aria-describedby={errors.name ? 'name-error' : undefined}
          />
          {errors.name && (
            <p
              id="name-error"
              style={{
                fontSize: '12px',
                color: '#ef4444',
                marginTop: '4px',
              }}
              role="alert"
            >
              {errors.name}
            </p>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label
            htmlFor="user-info-email"
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              marginBottom: '6px',
            }}
          >
            Email *
          </label>
          <input
            id="user-info-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) {
                setErrors((prev) => ({ ...prev, email: undefined }));
              }
            }}
            onKeyPress={handleKeyPress}
            disabled={isSubmitting || isLoading}
            placeholder="Enter your email"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: `1px solid ${errors.email ? '#ef4444' : '#d1d5db'}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              outline: 'none',
              transition: 'border-color 0.2s',
              backgroundColor: isSubmitting || isLoading ? '#f9fafb' : '#ffffff',
              color: '#111827',
            }}
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && (
            <p
              id="email-error"
              style={{
                fontSize: '12px',
                color: '#ef4444',
                marginTop: '4px',
              }}
              role="alert"
            >
              {errors.email}
            </p>
          )}
        </div>

        {displayError && !errors.name && !errors.email && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '16px',
            }}
          >
            <p
              style={{
                fontSize: '14px',
                color: '#dc2626',
              }}
              role="alert"
            >
              {displayError}
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || isLoading}
          style={{
            width: '100%',
            padding: '12px 16px',
            backgroundColor: isSubmitting || isLoading ? '#d1d5db' : '#3b82f6',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: isSubmitting || isLoading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
          }}
        >
          {isSubmitting || isLoading ? 'Starting chat...' : 'Start Chat'}
        </button>
      </form>
    </div>
  );
}

